/**
 * Optimize API Service
 * 
 * Handles all API calls for the Optimize page
 */

import useAuthStore from '../stores/authStore';

const API_BASE = '/api/tenants/optimize';

/**
 * Get authorization headers
 */
const getHeaders = () => {
  const token = useAuthStore.getState().token;
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

/**
 * Handle API errors
 */
const handleError = (error, context) => {
  console.error(`Optimize API Error (${context}):`, error);
  throw error;
};

/**
 * Optimize API methods
 */
export const optimizeApi = {
  /**
   * Get all room types for the hotel
   */
  getRoomTypes: async () => {
    try {
      const response = await fetch(`${API_BASE}/room-types`, {
        headers: getHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      handleError(error, 'getRoomTypes');
    }
  },

  /**
   * Get base rate plans for a room type
   * @param {string} roomTypeId - Optional room type ID to filter
   */
  getRatePlans: async (roomTypeId = null) => {
    try {
      const url = roomTypeId 
        ? `${API_BASE}/rate-plans?room_type_id=${roomTypeId}`
        : `${API_BASE}/rate-plans`;
      
      const response = await fetch(url, {
        headers: getHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      handleError(error, 'getRatePlans');
    }
  },

  /**
   * Get pricing recommendations
   * @param {string} roomTypeId - Room type UUID
   * @param {string} ratePlanId - Optional rate plan UUID
   * @param {number} days - Number of days (default 90)
   * @param {string} startDate - Optional start date (YYYY-MM-DD)
   */
  getRecommendations: async (roomTypeId, ratePlanId = null, days = 90, startDate = null) => {
    try {
      const params = new URLSearchParams({
        room_type_id: roomTypeId,
        days: days.toString()
      });
      
      if (ratePlanId) params.append('rate_plan_id', ratePlanId);
      if (startDate) params.append('start_date', startDate);
      
      const response = await fetch(`${API_BASE}/recommendations?${params}`, {
        headers: getHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      handleError(error, 'getRecommendations');
    }
  },

  /**
   * Get summary statistics
   * @param {string} roomTypeId - Room type UUID
   * @param {string} ratePlanId - Optional rate plan UUID
   * @param {number} days - Number of days (default 90)
   */
  getSummary: async (roomTypeId, ratePlanId = null, days = 90) => {
    try {
      const params = new URLSearchParams({
        room_type_id: roomTypeId,
        days: days.toString()
      });
      
      if (ratePlanId) params.append('rate_plan_id', ratePlanId);
      
      const response = await fetch(`${API_BASE}/summary?${params}`, {
        headers: getHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      handleError(error, 'getSummary');
    }
  },

  /**
   * Apply a single recommendation
   * @param {string} date - Date (YYYY-MM-DD)
   * @param {string} roomTypeId - Room type UUID
   * @param {string} ratePlanId - Optional rate plan UUID
   * @param {number} price - Price to apply
   * @param {string} overrideReason - Optional reason for override
   */
  applyRecommendation: async (date, roomTypeId, ratePlanId, price, overrideReason = null) => {
    try {
      const response = await fetch(`${API_BASE}/apply-recommendation`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          date,
          room_type_id: roomTypeId,
          rate_plan_id: ratePlanId,
          price,
          override_reason: overrideReason
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || `HTTP ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      handleError(error, 'applyRecommendation');
    }
  },

  /**
   * Apply bulk recommendations
   * @param {string} roomTypeId - Room type UUID
   * @param {string} ratePlanId - Optional rate plan UUID
   * @param {string} action - 'apply_all' or 'apply_selected'
   * @param {Array<string>} dates - Array of dates (for apply_selected)
   */
  applyBulk: async (roomTypeId, ratePlanId, action, dates = null) => {
    try {
      const body = {
        room_type_id: roomTypeId,
        action
      };
      
      if (ratePlanId) body.rate_plan_id = ratePlanId;
      if (dates && action === 'apply_selected') body.dates = dates;
      
      const response = await fetch(`${API_BASE}/apply-bulk`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(body)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || `HTTP ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      handleError(error, 'applyBulk');
    }
  },

  /**
   * Train ML models
   * @param {string} roomTypeId - Room type UUID
   * @param {string} ratePlanId - Optional rate plan UUID
   * @param {string} location - Optional location name
   * @param {boolean} forceRetrain - Force retraining
   */
  trainModels: async (roomTypeId, ratePlanId = null, location = null, forceRetrain = false) => {
    try {
      const body = {
        room_type_id: roomTypeId,
        force_retrain: forceRetrain
      };
      
      if (ratePlanId) body.rate_plan_id = ratePlanId;
      if (location) body.location = location;
      
      const response = await fetch(`${API_BASE}/train-models`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(body)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || `HTTP ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      handleError(error, 'trainModels');
    }
  },

  /**
   * Export recommendations to CSV
   * @param {Array} recommendations - Array of recommendation objects
   * @param {string} roomTypeName - Room type name for filename
   */
  exportToCSV: (recommendations, roomTypeName) => {
    try {
      // CSV headers
      const headers = [
        'Date',
        'Day',
        'Demand Score',
        'Demand Level',
        'Competitor Avg',
        'Occupancy %',
        'Current Price',
        'AI Recommended',
        'Difference',
        'Difference %',
        'Confidence',
        'Status'
      ];
      
      // CSV rows
      const rows = recommendations.map(rec => [
        rec.date,
        rec.day_of_week,
        rec.demand_score,
        rec.demand_level,
        rec.competitor_avg,
        rec.occupancy_percent,
        rec.current_price,
        rec.recommended_price,
        rec.difference,
        rec.difference_percent,
        rec.confidence_score,
        rec.status
      ]);
      
      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `pricing-recommendations-${roomTypeName}-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      handleError(error, 'exportToCSV');
    }
  }
};

export default optimizeApi;
